import { getDatabase, ref, get, set, push, update, remove, onValue } from "firebase/database";

/**
 * Collect auto-post config data (NEW - Data Collection)
 * @param {string} configId - Config ID
 * @param {Object} configData - Config data
 * @returns {Promise<boolean>} - Success status
 */
export const collectAutoPostConfig = async (configId, configData) => {
  try {
    const db = getDatabase();
    const userId = localStorage.getItem("userId");
    const now = Date.now();

    // Track config creation/update
    const analyticsRef = ref(db, `users/${userId}/dataCollection/autoPostConfigs/${configId}`);
    await set(analyticsRef, {
      configId: configId,
      createdAt: configData.createdAt || now,
      lastUpdatedAt: now,
      domain: configData.domain || "",
      platform: configData.platform || "",
      totalExecutions: 0,
      lastExecutedAt: null
    });

    console.log('✅ Auto-post config data collected:', configId);
    return true;
  } catch (error) {
    console.error('Error collecting auto-post config:', error);
    return false;
  }
};

/**
 * Track auto-post execution (NEW - Data Collection)
 * @param {string} configId - Config ID
 * @param {Object} executionData - Execution data
 * @returns {Promise<boolean>} - Success status
 */
export const trackAutoPostExecution = async (configId, executionData) => {
  try {
    const db = getDatabase();
    const userId = localStorage.getItem("userId");

    const analyticsRef = ref(db, `users/${userId}/dataCollection/autoPostConfigs/${configId}`);
    const snapshot = await get(analyticsRef);

    if (snapshot.exists()) {
      const current = snapshot.val();
      await update(analyticsRef, {
        totalExecutions: (current.totalExecutions || 0) + 1,
        lastExecutedAt: Date.now(),
        lastExecutionSuccess: executionData.success || false
      });
    }

    console.log('✅ Auto-post execution tracked:', configId);
    return true;
  } catch (error) {
    console.error('Error tracking auto-post execution:', error);
    return false;
  }
};

/**
 * Lấy thông tin membership của người dùng
 * @param {string} userId - ID của người dùng
 * @param {Function} setUserMembership - Hàm cập nhật state userMembership
 * @returns {Function} - Hàm cleanup
 */
export const getUserMembership = (userId, setUserMembership) => {
  if (!userId) return () => {};

  const db = getDatabase();
  const membershipRef = ref(db, `/users/${userId}/membership`);

  const unsubscribe = onValue(membershipRef, (snapshot) => {
    if (snapshot.exists()) {
      setUserMembership(snapshot.val());
    }
  });

  return unsubscribe;
};

/**
 * Tải cấu hình từ Firebase
 * @param {string} userId - ID của người dùng
 * @param {string} configId - ID của cấu hình
 * @returns {Promise<Object|null>} - Cấu hình đã tải
 */
export const loadConfig = async (userId, configId) => {
  try {
    if (!userId || !configId) return null;

    const db = getDatabase();
    const configRef = ref(db, `posts/${userId}/${configId}`);
    const snapshot = await get(configRef);

    if (snapshot.exists()) {
      return snapshot.val();
    }
    return null;
  } catch (error) {
    console.error("Error fetching config:", error);
    return null;
  }
};

/**
 * Lưu cấu hình vào Firebase
 * @param {string} userId - ID của người dùng
 * @param {string} configId - ID của cấu hình (nếu có)
 * @param {Object} configData - Dữ liệu cấu hình
 * @returns {Promise<Object>} - Kết quả lưu cấu hình
 */
export const saveConfig = async (userId, configId, configData) => {
  try {
    if (!userId) return { success: false, error: "Không có userId" };

    const db = getDatabase();
    const configRef = configId
      ? ref(db, `posts/${userId}/${configId}`)
      : ref(db, `posts/${userId}`);

    if (configId) {
      await update(configRef, configData);
      return { success: true, id: configId };
    } else {
      const newRef = await push(configRef, configData);
      return { success: true, id: newRef.key };
    }
  } catch (error) {
    console.error("Error saving config:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Xóa cấu hình từ Firebase
 * @param {string} userId - ID của người dùng
 * @param {string} configId - ID của cấu hình
 * @returns {Promise<Object>} - Kết quả xóa cấu hình
 */
export const deleteConfigFromFirebase = async (userId, configId) => {
  try {
    if (!userId || !configId) return { success: false, error: "Thiếu thông tin" };

    const db = getDatabase();
    const configRef = ref(db, `posts/${userId}/${configId}`);
    await remove(configRef);
    return { success: true };
  } catch (error) {
    console.error("Error deleting config:", error);
    return { success: false, error: error.message };
  }
};
